import React from 'react';
import { useTranslation } from 'react-i18next';

interface SearchHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onBack: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  disabled: boolean;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchQuery,
  onSearchChange,
  onBack,
  onRefresh,
  isRefreshing,
  disabled
}) => {
  const { t } = useTranslation();

  const handleClearSearch = () => {
    if (!disabled) {
      onSearchChange('');
    }
  };

  const handleRefresh = () => {
    if (!disabled && !isRefreshing) {
      onRefresh();
    }
  };

  const handleBack = () => {
    if (!disabled) {
      onBack();
    }
  };

  return (
    <div className="p-3 sm:p-4">
      <div className="flex justify-between items-center">
        <div onClick={handleBack}>
          <img src="/icons/backbtn.svg" className="mb-2 p-2" alt="Back" />
        </div>
        <div>
          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={disabled || isRefreshing}
            className={`
              p-2 rounded-lg transition-all duration-200 flex-shrink-0
              ${disabled || isRefreshing
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-gray-100 active:bg-gray-200 hover:scale-105'
              }
            `}
            aria-label={t('refreshMatchees', 'Refresh')}
          >
            <svg
              className={`w-5 h-5 sm:w-6 sm:h-6 text-neutral-grey ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search input container */}
        <div className="flex-1 relative min-w-0">
          <div className="relative">
            {/* Search icon prefix */}
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <img
                src="/icons/zoom.png"
                alt="Search"
                className="w-4 h-4 sm:w-5 sm:h-5 opacity-60"
              />
            </div>
            
            {/* Search input */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              disabled={disabled}
              placeholder={t('searchPlaceholder', 'Search handles...')}
              className={`
                w-full pl-9 pr-10 py-2.5 sm:pl-10 sm:pr-10 sm:py-3
                border-2 border-black/10 rounded-lg bg-white 
                text-gray-900 text-14 sm:text-16
                placeholder-neutral-grey font-texgyrepagella
                focus:ring-2 focus:ring-sangati-green focus:border-sangati-green
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                transition-all duration-200
              `}
            />
            
            {/* Clear search button */}
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                disabled={disabled}
                className={`
                  absolute inset-y-0 right-0 pr-3 flex items-center
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-70'}
                  transition-opacity duration-200
                `}
                aria-label={t('clearSearch', 'Clear search')}
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-grey hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchHeader;